import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { functions } from '@/core/firebase'
import { clearPendingTrainerInvite, savePendingTrainerInvite } from '../lib/trainerInvite'
import { httpsCallable } from 'firebase/functions'

type JoinState = 'loading' | 'ready' | 'invalid'

export function JoinTrainer() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<JoinState>('loading')
  const [trainerName, setTrainerName] = useState<string>('')

  useEffect(() => {
    let active = true

    const validateInvite = async () => {
      if (!code) {
        clearPendingTrainerInvite()
        setState('invalid')
        return
      }

      try {
        const getTrainerInvitePreview = httpsCallable<
          { trainerId: string },
          { valid: boolean; trainerId?: string; trainerName?: string }
        >(functions, 'getTrainerInvitePreview')
        const result = await getTrainerInvitePreview({ trainerId: code })

        if (!result.data.valid || !result.data.trainerId) {
          if (active) {
            clearPendingTrainerInvite()
            setState('invalid')
          }
          return
        }

        savePendingTrainerInvite(result.data.trainerId)

        if (active) {
          setTrainerName(result.data.trainerName || 'your trainer')
          setState('ready')
        }
      } catch (error) {
        console.error('Failed to validate trainer invite:', error)
        if (active) {
          clearPendingTrainerInvite()
          setState('invalid')
        }
      }
    }

    void validateInvite()

    return () => {
      active = false
    }
  }, [code])

  const subtitle = useMemo(() => {
    if (state === 'loading') {
      return 'Checking your invite link...'
    }

    if (state === 'invalid') {
      return 'This invite link is invalid or has expired.'
    }

    return `Create your account and you’ll be linked with ${trainerName}.`
  }, [state, trainerName])

  return (
    <div className="min-h-screen flex items-center justify-center p-4 safe-all">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
        <Card variant="elevated" padding="lg" className="shadow-2xl">
          <div className="text-center space-y-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">
                Trainer Invite
              </p>
              <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                Join Start Grounded
              </h1>
              <p className="mt-3 text-base text-gray-600 dark:text-slate-400">
                {subtitle}
              </p>
            </div>

            {state === 'loading' && (
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-t-2 border-b-2 border-primary-500" />
            )}

            {state === 'ready' && (
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => navigate('/phone-login')}
              >
                Continue to signup
              </Button>
            )}

            {state === 'invalid' && (
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Back to login
              </Button>
            )}
          </div>
        </Card>
      </motion.div>
    </div>
  )
}
